import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import { ColDef } from '@ag-grid-community/core'
import { StatisticalAnalysisViewDetailTableFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-table/statistical-analysis-view-detail-table.model'
import { delayAndResponseTimesDossierService } from '@/modules/statistical-analysis-module/services/delay-and-response-times-dossier/delay-and-response-times-dossier.service'
import { StatisticalAnalysisDelayAndResponseTimesDetailRequestTable, StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTable, StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableContentCase, StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableParsed, StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableTimeSeries, StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsCaseEnum, StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsPortionEnum, StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSampleEnum, StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSubportionEnum } from '@/modules/statistical-analysis-module/services/delay-and-response-times-dossier/delay-and-response-times-dossier.dto'
import { ColGroupDef } from '@ag-grid-enterprise/all-modules'
import { concat, extend, forEach, reduce } from 'lodash'
import { StatisticalAnalysisTimesheetItemSerie } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-timesheet/statistical-analysis-timesheet.dto'
import { Nullable } from '@/shared/types/app.types'
import { StatisticalAnalysisDatasource } from '@/modules/statistical-analysis-module/handlers/statistical-analysis-datasource'
import { StatiscalAnalysisTypeCases, StatisticalAnalysisDelayAndResponseEnum, StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo } from '@/modules/statistical-analysis-module/views/delays-and-response-time-dossier/delays-and-response-time-dossier.model'
import { InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'
import { StatisticalAnalysisTemporalFilterEnum } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-temporal-filter/statistical-analysis-temporal-filter.model'
import { getIconHeaders } from '@/modules/statistical-analysis-module/shared/helpers/helpers'
import { StatisticalAnalysisGroupingResponseField } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-grouping/statistical-analysis-grouping.dto'

export class StatisticalAnalysisHandlerDatasourceDelayAndTimeResponsesDossier extends StatisticalAnalysisDatasource<StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableParsed> {
    protected statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>

    constructor(statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>) {
        super()
        this.statisticalAnalysisFilters = statisticalAnalysisFilters
    }

    public getGridColumns(): ColDef[] | ColGroupDef[] | ExportedColGroupDef[] | ExportedColDef [] {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo
        return concat(
            [],
            [{ type: AgGridColumnType.CUSTOM_CHECKBOX } as ColDef],
            this._filterGroupedGridColumns(),
            this._generateTimesheetColumns(sidebarFilter.filterTimeSheetId?.timeSeries as StatisticalAnalysisTimesheetItemSerie[]),
            this._getStatColumns()
        )
    }

    public async paginator(filterModel: CommonFilterModel, params: CommonParams, scrollId: string): Promise<InfinitePaginatorResponse<StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableParsed>> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo
        const baseRequest = delayAndResponseTimesDossierService.getBaseRequest(sidebarFilter)
        const timeMeasureType = sidebarFilter.typeDelaysAndResponseTimes!.id as StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum
        let detailRequest: StatisticalAnalysisDelayAndResponseTimesDetailRequestTable = {
            filterTimeSheetId: sidebarFilter.filterTimeSheetId?.id!,
            groupByFields: (this.statisticalAnalysisFilters?.grouping as StatisticalAnalysisGroupingResponseField[]).map((item: StatisticalAnalysisGroupingResponseField) => item.fieldName)
        }
        if (timeMeasureType === StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum.DELAY) {
            detailRequest = {
                ...detailRequest,
                delayType: sidebarFilter?.typeDelay!
            }
        }
        const typeCase = sidebarFilter?.typeSearch?.id as StatiscalAnalysisTypeCases
        const responseTimeType = sidebarFilter.trackingDetailSelected?.key as StatisticalAnalysisDelayAndResponseEnum
        const infoDetail = sidebarFilter.timeFilter?.id as StatisticalAnalysisTemporalFilterEnum
        let entityTypeId = {}
        switch (typeCase) {
        case StatiscalAnalysisTypeCases.CASE:
            entityTypeId = { caseTypeId: sidebarFilter.trackingGeneralSelected?.key }
            break
        case StatiscalAnalysisTypeCases.SAMPLE:
            entityTypeId = { sampleTypeId: sidebarFilter.trackingGeneralSelected?.key }
            break
        case StatiscalAnalysisTypeCases.PORTION:
            entityTypeId = { portionTypeId: sidebarFilter.trackingGeneralSelected?.key }
            break
        case StatiscalAnalysisTypeCases.SUBPORTION:
            entityTypeId = { subportionTypeId: sidebarFilter.trackingGeneralSelected?.key }
            break
        default:
            entityTypeId = {}
        }
        const response: StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTable = await delayAndResponseTimesDossierService.getDetailViewTable(
            filterModel, params, typeCase, timeMeasureType, responseTimeType, baseRequest, detailRequest, entityTypeId, infoDetail, scrollId
        )
        return {
            content: this._parseResponse(response.content),
            scrollPage: {
                scrollId: encodeURIComponent(JSON.stringify(response.scrollPage.scrollId)),
                size: response.scrollPage.size,
                totalElements: null
            }
        }
    }

    private _generateTimesheetColumns(series: StatisticalAnalysisTimesheetItemSerie[]): ExportedColGroupDef[] {
        const children: ExportedColDef[] = series.map((serie: StatisticalAnalysisTimesheetItemSerie, index: number) => {
            return {
                type: AgGridColumnType.TEXT,
                headerName: delayAndResponseTimesDossierService.generateTimesheetLabelSerie(serie),
                field: `timeSeries.timeTemplateRange${serie.id}.value`,
                // TODO Pending to revise
                exportField: `timeSeries[${index}].value`,
                exportType: AgGridExportType.NUMBER,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            }
        })
        return [
            {
                headerName: i18n.t('statisticalAnalysis.dossiers.delaysAndResponseTimes.timeTemplate').toString(),
                children
            }
        ]
    }

    private _parseResponse(data: StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableContentCase[]): StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableParsed[] {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo
        return data.map((row:StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableContentCase) => {
            const series = reduce(sidebarFilter.filterTimeSheetId?.timeSeries, (aggr: any, item: StatisticalAnalysisTimesheetItemSerie) => {
                const newSerie = row.timeSeries.find((serie: StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableTimeSeries) => serie.timeSerie.id === item.id)
                return extend(aggr, { [`timeTemplateRange${item.id}`]: { value: newSerie?.value } })
            }, {})
            const parsedItem: StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableParsed = {
                ...row,
                timeSeries: series
            }
            return parsedItem
        })
    }

    private _getGroupingColumnsCases(): ExportedColDef[] {
        return [
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.user'),
                field: 'user.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsCaseEnum.USER
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.caseType'),
                field: 'caseType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 120,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsCaseEnum.CASE_TYPE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.priority'),
                field: 'priority.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsCaseEnum.PRIORITY
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingHealthManagementArea'),
                field: 'physicianHealthManagementArea.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsCaseEnum.PHYSICIAN_HEALTH_MANAGEMENT_AREA
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingCenter'),
                field: 'physicianCenter.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsCaseEnum.PHYSICIAN_CENTER
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingDepartment'),
                field: 'physicianDepartment.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsCaseEnum.PHYSICIAN_DEPARTMENT
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingProfessional'),
                field: 'physicianPetitioner.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsCaseEnum.PHYSICIAN_PETITIONER
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.regime'),
                field: 'regime.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsCaseEnum.REGIME
                }
            },
            {
                type: AgGridColumnType.CHECK_ICON,
                headerName: i18n.tc('statisticalAnalysis.grids.consultationCase'),
                field: 'caseOfReference.literal',
                exportType: AgGridExportType.BOOLEAN,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsCaseEnum.CASE_OF_REFERENCE
                }
            }
        ]
    }

    private _getGroupingColumnsSamples(): ExportedColDef[] {
        return [
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.procedure'),
                field: 'procedure.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSampleEnum.PROCEDURE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.mostLikelyMorphologicCode'),
                field: 'mostLikelyMorphologicCode.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 120,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSampleEnum.MOST_LIKELY_MORPHOLOGIC_CODE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.specialist'),
                field: 'specialist.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSampleEnum.SPECIALIST
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.sampleType'),
                field: 'sampleType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSampleEnum.SAMPLE_TYPE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.topographicCode'),
                field: 'topographicCode.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSampleEnum.TOPOGRAPHIC_CODE
                }
            }
        ]
    }

    private _getGroupingColumnsPortions(): ExportedColDef[] {
        return [
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.protocol'),
                field: 'protocol.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsPortionEnum.PROTOCOL
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.portionType'),
                field: 'portionType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 120,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsPortionEnum.PORTION_TYPE
                }
            }
        ]
    }

    private _getGroupingColumnsSubportions(): ExportedColDef[] {
        return [
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.assay'),
                field: 'assay.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSubportionEnum.ASSAY
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.assayGroup'),
                field: 'assayGroup.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 120,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSubportionEnum.ASSAY_GROUP
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.subportionPriority'),
                field: 'subportionPriority.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSubportionEnum.SUBPORTION_PRIORITY
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.subportionProtocol'),
                field: 'subportionProtocol.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSubportionEnum.SUBPORTION_PROTOCOL
                }
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.subportionType'),
                field: 'subportionType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisDelayAndResponseTimesStatisticsDossierGroupingColumnsSubportionEnum.SUBPORTION_TYPE
                }
            }
        ]
    }

    private _getStatsCaseColumn(): ExportedColDef[] {
        return [{
            type: AgGridColumnType.NUMBER,
            headerName: i18n.tc('statisticalAnalysis.grids.cases'),
            field: 'stats.totalCases',
            exportType: AgGridExportType.NUMBER,
            minWidth: 100,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        }]
    }

    private _getStatsSampleColumn(): ExportedColDef[] {
        return [{
            type: AgGridColumnType.NUMBER,
            headerName: i18n.tc('statisticalAnalysis.grids.samples'),
            field: 'stats.totalSamples',
            exportType: AgGridExportType.NUMBER,
            minWidth: 100,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        }]
    }

    private _getStatsPortionColumn(): ExportedColDef[] {
        return [{
            type: AgGridColumnType.NUMBER,
            headerName: i18n.tc('statisticalAnalysis.grids.portions'),
            field: 'stats.totalPortions',
            exportType: AgGridExportType.NUMBER,
            minWidth: 100,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        }]
    }

    private _getStatsSubportionColumn(): ExportedColDef[] {
        return [{
            type: AgGridColumnType.NUMBER,
            headerName: i18n.tc('statisticalAnalysis.grids.subportions'),
            field: 'stats.totalSubportions',
            exportField: 'stats.totalSubportions',
            exportType: AgGridExportType.NUMBER,
            minWidth: 100,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        }]
    }

    private _getStatFixColumns(): ExportedColDef[] {
        return [
            {
                type: AgGridColumnType.DECIMAL,
                headerName: i18n.tc('statisticalAnalysis.grids.average'),
                field: 'stats.mean',
                exportField: 'stats.mean',
                exportType: AgGridExportType.DOUBLE,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.DECIMAL,
                headerName: i18n.tc('statisticalAnalysis.grids.standardDeviation'),
                field: 'stats.deviation',
                exportField: 'stats.deviation',
                exportType: AgGridExportType.DOUBLE,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.DECIMAL,
                headerName: i18n.tc('statisticalAnalysis.grids.median'),
                field: 'stats.median',
                exportField: 'stats.median',
                exportType: AgGridExportType.DOUBLE,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.DECIMAL,
                headerName: i18n.tc('statisticalAnalysis.grids.maximumTime'),
                field: 'stats.maxTime',
                exportField: 'stats.maxTime',
                exportType: AgGridExportType.DOUBLE,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.DECIMAL,
                headerName: i18n.tc('statisticalAnalysis.grids.minimumTime'),
                field: 'stats.minTime',
                exportField: 'stats.minTime',
                exportType: AgGridExportType.DOUBLE,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            }
        ]
    }

    private _getStatColumns(): ExportedColGroupDef[] {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo
        const typeCase = sidebarFilter?.typeSearch?.id as StatiscalAnalysisTypeCases
        let columns: ExportedColDef[]
        switch (typeCase) {
        case StatiscalAnalysisTypeCases.CASE:
            columns = concat([], this._getStatsCaseColumn(), this._getStatFixColumns())
            break
        case StatiscalAnalysisTypeCases.SAMPLE:
            columns = concat([], this._getStatsSampleColumn(), this._getStatFixColumns())
            break
        case StatiscalAnalysisTypeCases.PORTION:
            columns = concat([], this._getStatsPortionColumn(), this._getStatFixColumns())
            break
        case StatiscalAnalysisTypeCases.SUBPORTION:
            columns = concat([], this._getStatsSubportionColumn(), this._getStatFixColumns())
            break
        default:
            columns = this._getStatFixColumns()
            break
        }
        return [
            {
                headerName: i18n.tc('statisticalAnalysis.grids.statistics'),
                children: columns
            }
        ]
    }

    private _getGroupingColumns(): ExportedColDef[] {
        return concat(
            [],
            this._getGroupingColumnsCases(),
            this._getGroupingColumnsSamples(),
            this._getGroupingColumnsPortions(),
            this._getGroupingColumnsSubportions()
        )
    }

    // TODO Pending to add into class StatisticalAnalysisDatasource
    private _extendColumnWithIconHeader(column: ExportedColDef, entityType: StatiscalAnalysisTypeCases): ExportedColDef {
        return {
            ...column,
            headerComponentParams: {
                ...column.headerComponentParams,
                template: `<span class="ag-header-cell-text"><i class="far ${getIconHeaders(entityType)} mr-2"></i>${column.headerName}</span>`
            }
        }
    }

    // TODO Pending to add into class StatisticalAnalysisDatasource
    private _filterGroupedGridColumns(): ColGroupDef[] {
        const colDefs: ColDef[] = []
        const groupedColumns = this._getGroupingColumns()
        forEach(this.statisticalAnalysisFilters?.grouping, (item: StatisticalAnalysisGroupingResponseField) => {
            const column = groupedColumns.find((groupColumn: ExportedColDef) => groupColumn.headerComponentParams.columnFieldName === item.fieldName) as ExportedColDef
            if (column) {
                column.headerName = item.translatedFieldName
                colDefs.push(this._extendColumnWithIconHeader(column, item.entity))
            }
        })
        return [{
            headerName: i18n.tc('statisticalAnalysis.grids.groupings'),
            children: colDefs
        }]
    }
}
